import { useEffect } from 'react'
import { dibujarFlecha, dibujarCirculoArrastre } from '../utils/canvasUtils'
import { extraerLetraLabel, obtenerCoordenadasMedida } from '../utils/geometriaUtils'

/**
 * Hook para manejar el dibujo del canvas
 */
export const useCanvasDrawing = ({
  canvasRef,
  imageRef,
  imagenFirmadaUrl,
  modalImagenVisible,
  coordenadas,
  medidasManuales,
  medidaSeleccionada,
  lineaReferenciaSeleccionada,
  modoDibujo,
  puntoA,
  puntoBTemporal,
  puntoBConfirmado,
  centroCirculo,
  puntoRadioTemporal,
  radioConfirmado,
  medidaIndexParaLineaReferencia,
  puntoALineaReferencia,
  puntoBLineaReferencia,
  puntoBLineaReferenciaConfirmado,
}) => {
  useEffect(() => {
    if (imagenFirmadaUrl && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current
      const img = imageRef.current
      
      const drawCanvas = () => {
        const rect = img.getBoundingClientRect()
        const displayWidth = rect.width
        const displayHeight = rect.height
        
        canvas.width = displayWidth
        canvas.height = displayHeight
        canvas.style.width = `${displayWidth}px`
        canvas.style.height = `${displayHeight}px`
        
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        // Dibujar margen púrpura
        ctx.strokeStyle = '#800080'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2)
        
        const escalaX = canvas.width / 1000
        const escalaY = canvas.height / 1000
        const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
        
        if (todasLasCoordenadas && todasLasCoordenadas.length > 0) {
          // Dibujar medidas principales
          ctx.setLineDash([])
          ctx.strokeStyle = '#800080'
          ctx.lineWidth = 1
          
          todasLasCoordenadas.forEach((cota, medidaIndex) => {
            const estaSeleccionada = medidaSeleccionada === medidaIndex
            
            // Dibujar geometria_cota (objeto o array)
            if (cota.geometria_cota && !Array.isArray(cota.geometria_cota)) {
              const geometria = cota.geometria_cota
              if (geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                  geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
                const x1 = geometria.x_ini * escalaX
                const y1 = geometria.y_ini * escalaY
                const x2 = geometria.x_fin * escalaX
                const y2 = geometria.y_fin * escalaY
                
                ctx.strokeStyle = estaSeleccionada ? '#0066cc' : '#800080'
                ctx.lineWidth = estaSeleccionada ? 2 : 1
                
                // Dibujar línea morada CON flechas
                dibujarFlecha(ctx, x1, y1, x2, y2)
                
                if (estaSeleccionada) {
                  dibujarCirculoArrastre(ctx, x1, y1)
                  dibujarCirculoArrastre(ctx, x2, y2)
                }
              }
            } else if (cota.geometria_cota && Array.isArray(cota.geometria_cota)) {
              cota.geometria_cota.forEach((geometria) => {
                if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                    geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
                  const x1 = geometria.x_ini * escalaX
                  const y1 = geometria.y_ini * escalaY
                  const x2 = geometria.x_fin * escalaX
                  const y2 = geometria.y_fin * escalaY
                  
                  ctx.strokeStyle = estaSeleccionada ? '#0066cc' : '#800080'
                  ctx.lineWidth = estaSeleccionada ? 2 : 1
                  
                  // Dibujar línea morada CON flechas
                  dibujarFlecha(ctx, x1, y1, x2, y2)
                  
                  if (estaSeleccionada) {
                    dibujarCirculoArrastre(ctx, x1, y1)
                    dibujarCirculoArrastre(ctx, x2, y2)
                  }
                }
              })
            } else if (cota.coordenadas_normalizadas) {
              const coords = cota.coordenadas_normalizadas
              
              if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
                  coords.x_fin !== undefined && coords.y_fin !== undefined) {
                const x1 = coords.x_ini * escalaX
                const y1 = coords.y_ini * escalaY
                const x2 = coords.x_fin * escalaX
                const y2 = coords.y_fin * escalaY
                
                ctx.strokeStyle = estaSeleccionada ? '#0066cc' : '#800080'
                ctx.lineWidth = estaSeleccionada ? 2 : 1
                
                // Dibujar línea morada CON flechas
                dibujarFlecha(ctx, x1, y1, x2, y2)
                
                if (estaSeleccionada) {
                  dibujarCirculoArrastre(ctx, x1, y1)
                  dibujarCirculoArrastre(ctx, x2, y2)
                }
              } else if (coords.x !== undefined && coords.y !== undefined && coords.radio !== undefined) {
                ctx.strokeStyle = estaSeleccionada ? '#0066cc' : '#800080'
                ctx.lineWidth = estaSeleccionada ? 2 : 1
                
                ctx.beginPath()
                ctx.arc(
                  coords.x * escalaX,
                  coords.y * escalaY,
                  coords.radio * escalaX,
                  0,
                  2 * Math.PI
                )
                ctx.stroke()
              }
            }
          })
          
          // Dibujar líneas de referencia
          ctx.setLineDash([])
          ctx.strokeStyle = '#8B0000'
          ctx.lineWidth = 1
          
          todasLasCoordenadas.forEach((cota, medidaIndex) => {
            if (cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
              cota.lineas_a_repuesto.forEach((linea, lineaIndex) => {
                if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
                    linea.x_fin !== undefined && linea.y_fin !== undefined) {
                  const x1 = linea.x_ini * escalaX
                  const y1 = linea.y_ini * escalaY
                  const x2 = linea.x_fin * escalaX
                  const y2 = linea.y_fin * escalaY
                  
                  const estaSeleccionada = lineaReferenciaSeleccionada && 
                    lineaReferenciaSeleccionada.medidaIndex === medidaIndex &&
                    lineaReferenciaSeleccionada.lineaIndex === lineaIndex
                  
                  ctx.strokeStyle = estaSeleccionada ? '#0066cc' : '#8B0000'
                  ctx.lineWidth = estaSeleccionada ? 2 : 1
                  
                  // Dibujar línea roja SIN flechas (solo línea)
                  ctx.beginPath()
                  ctx.moveTo(x1, y1)
                  ctx.lineTo(x2, y2)
                  ctx.stroke()
                  
                  if (estaSeleccionada) {
                    dibujarCirculoArrastre(ctx, x1, y1)
                    dibujarCirculoArrastre(ctx, x2, y2)
                  }
                }
              })
            }
          })
          
          // Dibujar etiquetas
          ctx.fillStyle = '#800080'
          ctx.font = 'bold 16px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          
          todasLasCoordenadas.forEach((cota) => {
            const letraLabel = extraerLetraLabel(cota)
            if (letraLabel) {
              // Si tiene coordenadas_etiqueta, usarlas; si no, calcularlas automáticamente
              let etiqueta = cota.coordenadas_etiqueta
              if (!etiqueta) {
                // Calcular automáticamente la posición de la etiqueta
                const coords = obtenerCoordenadasMedida(cota)
                if (coords) {
                  if (coords.tipo === 'linea') {
                    const x = (coords.x1 + coords.x2) / 2
                    const y = (coords.y1 + coords.y2) / 2
                    etiqueta = { x: x + 20, y: y - 20 }
                  } else if (coords.tipo === 'circulo') {
                    etiqueta = { x: coords.x, y: coords.y - coords.radio - 20 }
                  }
                }
              }
              
              if (etiqueta && etiqueta.x !== undefined && etiqueta.y !== undefined) {
                ctx.fillText(
                  letraLabel,
                  etiqueta.x * escalaX,
                  etiqueta.y * escalaY
                )
              }
            }
          })
        }

        // Dibujar línea temporal de referencia
        if (medidaIndexParaLineaReferencia !== null && puntoALineaReferencia && puntoBLineaReferencia) {
          ctx.setLineDash([])
          ctx.strokeStyle = '#8B0000'
          ctx.lineWidth = 2
          // Dibujar línea roja temporal SIN flechas
          ctx.beginPath()
          ctx.moveTo(puntoALineaReferencia.x * escalaX, puntoALineaReferencia.y * escalaY)
          ctx.lineTo(puntoBLineaReferencia.x * escalaX, puntoBLineaReferencia.y * escalaY)
          ctx.stroke()
          
          ctx.fillStyle = '#8B0000'
          ctx.beginPath()
          ctx.arc(puntoALineaReferencia.x * escalaX, puntoALineaReferencia.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
          ctx.beginPath()
          ctx.arc(puntoBLineaReferencia.x * escalaX, puntoBLineaReferencia.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        } else if (medidaIndexParaLineaReferencia !== null && puntoALineaReferencia) {
          ctx.fillStyle = '#8B0000'
          ctx.beginPath()
          ctx.arc(puntoALineaReferencia.x * escalaX, puntoALineaReferencia.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        }

        // Dibujar línea temporal en modo dibujo
        if (modoDibujo === 'puntoB' && puntoA && puntoBTemporal) {
          ctx.setLineDash([])
          ctx.strokeStyle = '#800080'
          ctx.lineWidth = 2
          // Dibujar línea morada temporal CON flechas
          dibujarFlecha(
            ctx,
            puntoA.x * escalaX,
            puntoA.y * escalaY,
            puntoBTemporal.x * escalaX,
            puntoBTemporal.y * escalaY
          )
          
          ctx.fillStyle = '#800080'
          ctx.beginPath()
          ctx.arc(puntoA.x * escalaX, puntoA.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
          ctx.beginPath()
          ctx.arc(puntoBTemporal.x * escalaX, puntoBTemporal.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        } else if (modoDibujo === 'puntoB' && puntoA) {
          ctx.fillStyle = '#800080'
          ctx.beginPath()
          ctx.arc(puntoA.x * escalaX, puntoA.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        }

        // Dibujar círculo temporal
        if (modoDibujo === 'circuloRadio' && centroCirculo && puntoRadioTemporal) {
          ctx.setLineDash([])
          ctx.strokeStyle = '#800080'
          ctx.lineWidth = 2
          
          const dx = puntoRadioTemporal.x - centroCirculo.x
          const dy = puntoRadioTemporal.y - centroCirculo.y
          const radio = Math.sqrt(dx * dx + dy * dy)
          
          ctx.beginPath()
          ctx.arc(
            centroCirculo.x * escalaX,
            centroCirculo.y * escalaY,
            radio * escalaX,
            0,
            2 * Math.PI
          )
          ctx.stroke()
          
          ctx.fillStyle = '#800080'
          ctx.beginPath()
          ctx.arc(centroCirculo.x * escalaX, centroCirculo.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
          ctx.beginPath()
          ctx.arc(puntoRadioTemporal.x * escalaX, puntoRadioTemporal.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        } else if (modoDibujo === 'circuloRadio' && centroCirculo) {
          ctx.fillStyle = '#800080'
          ctx.beginPath()
          ctx.arc(centroCirculo.x * escalaX, centroCirculo.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        } else if (modoDibujo === 'circuloCentro' && centroCirculo) {
          ctx.fillStyle = '#800080'
          ctx.beginPath()
          ctx.arc(centroCirculo.x * escalaX, centroCirculo.y * escalaY, 5, 0, 2 * Math.PI)
          ctx.fill()
        }
      }
      
      if (img.complete && img.naturalWidth > 0) {
        setTimeout(drawCanvas, 0)
      } else {
        img.onload = drawCanvas
      }
      
      const resizeObserver = new ResizeObserver(() => {
        if (img.complete) {
          requestAnimationFrame(() => {
            drawCanvas()
          })
        }
      })
      
      if (img.parentElement) {
        resizeObserver.observe(img.parentElement)
      }
      resizeObserver.observe(img)
      
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [
    imagenFirmadaUrl,
    modalImagenVisible,
    coordenadas,
    medidasManuales,
    modoDibujo,
    puntoA,
    puntoBTemporal,
    puntoBConfirmado,
    centroCirculo,
    puntoRadioTemporal,
    radioConfirmado,
    medidaIndexParaLineaReferencia,
    puntoALineaReferencia,
    puntoBLineaReferencia,
    puntoBLineaReferenciaConfirmado,
    lineaReferenciaSeleccionada,
    medidaSeleccionada,
    canvasRef,
    imageRef,
  ])
}
